import React, { useState, useRef, useEffect } from 'react';
import Card from './Card';

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownButtonProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  width?: string;
  height?: string;
  size?: 'small' | 'medium' | 'large';
}

const DropdownButton: React.FC<DropdownButtonProps> = ({
  options,
  value,
  onChange,
  placeholder = '선택하세요',
  disabled = false,
  width,
  height,
  size = 'small'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const sizeConfig = {
    small: { width: '112px', height: '48px' },
    medium: { width: '220px', height: '40px' },
    large: { width: '394px', height: '48px' }
  };
  
  // size가 제공되면 sizeConfig 사용, 그렇지 않으면 수동 설정 사용
  const finalWidth = width || sizeConfig[size].width;
  const finalHeight = height || sizeConfig[size].height;
  // 현재 선택된 옵션의 라벨 찾기
  const selectedOption = options.find(option => option.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOptionClick = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div 
      ref={dropdownRef}
      style={{
        width: finalWidth,
        height: finalHeight,
        position: 'relative',
        display: 'inline-flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
      }}
    >
      {/* 드롭다운 버튼 */}
      <div 
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onMouseEnter={() => !disabled && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          width: '100%',
          height: '100%',
          padding: '12px 16px',
          background: disabled ? '#F5F5F5' : 'var(--Color-fill-white, white)',
          borderRadius: size === 'large' ? '12px' : '8px',
          border: `1.2px solid ${disabled ? 'var(--Color-line-normal, #F0F0F0)' : (isOpen ? '#161616' : 'var(--Color-line-normal, #F0F0F0)')}`,
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '8px',
          display: 'flex',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          transition: 'all 0.2s ease'
        }}
      >
        <div style={{
          flex: '1 1 0',
          justifyContent: 'space-between',
          alignItems: 'center',
          display: 'flex'
        }}>
          <div style={{
            flex: '1 1 0',
            color: disabled ? '#989898' : (isOpen || isHovered ? '#161616' : '#989898'),
            fontSize: '13px',
            fontFamily: 'Pretendard',
            fontWeight: '500',
            lineHeight: '19.50px',
            wordWrap: 'break-word',
            transition: 'color 0.2s ease'
          }}>
            {displayText}
          </div>
          
          {/* 드롭다운 화살표 아이콘 */}
          <div style={{
            justifyContent: 'flex-end',
            alignItems: 'center',
            display: 'flex'
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              position: 'relative',
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }}>
              {/* 화살표 SVG */}
              <img 
                src="/images/icons/arrowDown.svg" 
                alt="화살표" 
                style={{
                  width: '24px',
                  height: '24px'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 드롭다운 옵션 리스트 */}
      {isOpen && !disabled && (
        <>
          <style>
            {`
              .dropdown-options-scrollbar::-webkit-scrollbar {
                width: 8px;
              }
              
              .dropdown-options-scrollbar::-webkit-scrollbar-track {
                background: transparent;
              }
              
              .dropdown-options-scrollbar::-webkit-scrollbar-thumb {
                background: #D9D9D9;
                border-radius: 4px;
              }
              
              .dropdown-options-scrollbar::-webkit-scrollbar-thumb:hover {
                background: #C0C0C0;
              }
            `}
          </style>
          <Card
            width="100%"
            gap={4}
            padding="8px"
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1000,
              overflow: 'hidden',
              marginTop: '8px',
              minHeight: 'auto',
              maxHeight: '240px',
              height: 'auto',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div
              className="dropdown-options-scrollbar"
              style={{
                overflowY: 'auto',
                overflowX: 'hidden',
                maxHeight: '240px',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}
            >
              {options.map((option, index) => (
                <div
                  key={option.value}
                  onClick={() => handleOptionClick(option.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    color: 'var(--Color-text-normal, #161616)',
                    fontSize: '13px',
                    fontFamily: 'Pretendard',
                    fontWeight: '500',
                    lineHeight: '19.50px',
                    borderRadius: '8px',
                    transition: 'background-color 0.2s ease',
                    flexShrink: 0
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#F8F8F8';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {option.label}
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default DropdownButton;
